// src/pages/RideDetails.js - Complete Working Version
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function RideDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [waypoints, setWaypoints] = useState([]);

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchRideDetails();
  }, [id]);

  const fetchRideDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch all rides and find the specific one
      const response = await axios.get(`${API_URL}/all-rides`, { headers });
      const rides = response.data.rides || [];
      
      // Find the ride with matching ID (handle both string and number)
      const rideData = rides.find(r => r.id === parseInt(id) || r.id === id);
      
      if (rideData) {
        setRide(rideData);
        
        // Try to fetch waypoints if available
        try {
          const waypointsRes = await axios.get(
            `https://boda-backend-wuea.onrender.com/api/ride/${id}/waypoints`,
            { headers }
          );
          setWaypoints(waypointsRes.data.waypoints || []);
        } catch (wpError) {
          console.log('No waypoints found for this ride');
          setWaypoints([]);
        }
      } else {
        setError('Ride not found');
      }
    } catch (error) {
      console.error('Error fetching ride details:', error);
      setError('Failed to load ride details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: { bg: '#dcfce7', color: '#166534', icon: '✅', text: 'Completed' },
      in_progress: { bg: '#fef3c7', color: '#92400e', icon: '🔄', text: 'In Progress' },
      pending: { bg: '#fef3c7', color: '#92400e', icon: '⏳', text: 'Pending' },
      cancelled: { bg: '#fee2e2', color: '#991b1b', icon: '❌', text: 'Cancelled' },
      accepted: { bg: '#dbeafe', color: '#1e40af', icon: '✓', text: 'Accepted' },
      arrived_at_pickup: { bg: '#dcfce7', color: '#166534', icon: '📍', text: 'Arrived at Pickup' },
      scheduled: { bg: '#e0e7ff', color: '#4338ca', icon: '📅', text: 'Scheduled' },
    };
    const config = colors[status] || { bg: '#f1f5f9', color: '#64748b', icon: '📌', text: status };
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        backgroundColor: config.bg,
        color: config.color,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        {config.icon} {config.text}
      </span>
    );
  };

  const getVehicleIcon = (type) => {
    const vehicles = {
      boda: '🛵',
      boda_electric: '⚡',
      car: '🚗',
      car_xl: '🚙',
    };
    return vehicles[type] || '🚗';
  };

  const getVehicleLabel = (type) => {
    const labels = {
      boda: 'Boda Boda',
      boda_electric: 'Boda Electric',
      car: 'Car',
      car_xl: 'Car XL',
    };
    return labels[type] || type || 'Boda';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return dateString;
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const diff = Math.floor((new Date() - new Date(dateString)) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
      return `${Math.floor(diff / 86400)} days ago`;
    } catch (e) {
      return '';
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f1f5f9'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading ride details...</div>
        </div>
      </div>
    );
  }

  if (error || !ride) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        backgroundColor: '#f1f5f9'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
        <h2 style={{ color: '#64748b' }}>{error || 'Ride not found'}</h2>
        <button
          onClick={() => navigate('/rides')}
          style={{
            padding: '10px 24px',
            backgroundColor: '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '16px',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Back to Rides
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px', 
        marginBottom: '24px',
        flexWrap: 'wrap',
      }}>
        <button
          onClick={() => navigate('/rides')}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#64748b',
            padding: '8px',
            borderRadius: '8px',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
          Ride #{ride.id}
        </h1>
        <div style={{ marginLeft: 'auto' }}>
          {getStatusBadge(ride.status)}
        </div>
      </div>

      {/* Ride Info Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        marginBottom: '24px',
      }}>
        {/* Left - Route Info */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px', fontWeight: '600' }}>
            📍 Route Information
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            {/* Pickup */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}>A</div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Pickup</div>
                  <div style={{ fontWeight: '500' }}>{ride.pickup_address || 'Unknown location'}</div>
                  {ride.pickup_lat && ride.pickup_lng && (
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      📍 {parseFloat(ride.pickup_lat).toFixed(6)}, {parseFloat(ride.pickup_lng).toFixed(6)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Waypoints */}
            {waypoints.length > 0 && (
              <div style={{ marginLeft: '16px', borderLeft: '2px dashed #94a3b8', paddingLeft: '16px' }}>
                {waypoints.map((wp, index) => (
                  <div key={wp.id || index} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: '#f59e0b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}>{String.fromCharCode(66 + index)}</div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Stop {index + 1}</div>
                        <div style={{ fontWeight: '500' }}>{wp.address || 'Stop location'}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Dropoff */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}>
                  {waypoints.length > 0 ? String.fromCharCode(66 + waypoints.length) : 'B'}
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Dropoff</div>
                  <div style={{ fontWeight: '500' }}>{ride.dropoff_address || 'Unknown location'}</div>
                  {ride.dropoff_lat && ride.dropoff_lng && (
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      📍 {parseFloat(ride.dropoff_lat).toFixed(6)}, {parseFloat(ride.dropoff_lng).toFixed(6)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Route Actions */}
          {ride.pickup_lat && ride.pickup_lng && ride.dropoff_lat && ride.dropoff_lng && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => {
                  const url = `https://www.google.com/maps/dir/${ride.pickup_lat},${ride.pickup_lng}/${ride.dropoff_lat},${ride.dropoff_lng}`;
                  window.open(url, '_blank');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                🗺️ View Route on Google Maps
              </button>
            </div>
          )}
        </div>

        {/* Right - Ride Details */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px', fontWeight: '600' }}>
            💰 Ride Details
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            {/* Fare */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
            }}>
              <span style={{ color: '#64748b' }}>Total Fare</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#f97316' }}>
                UGX {(ride.total_fare || 0).toLocaleString()}
              </span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b' }}>Vehicle</span>
              <span style={{ fontWeight: '500' }}>
                {getVehicleIcon(ride.vehicle_type)} {getVehicleLabel(ride.vehicle_type)}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b' }}>Payment Method</span>
              <span style={{ fontWeight: '500' }}>
                {ride.payment_method === 'cash' ? '💰 Cash' :
                 ride.payment_method === 'mtn_momo' ? '📱 MTN MoMo' :
                 ride.payment_method === 'airtel_money' ? '📱 Airtel Money' :
                 ride.payment_method || '—'}
              </span>
            </div>

            {ride.passenger_name && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>Passenger</span>
                <span style={{ fontWeight: '500' }}>
                  {ride.passenger_name} {ride.passenger_phone && `(${ride.passenger_phone})`}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b' }}>Customer</span>
              <span style={{ fontWeight: '500' }}>
                {ride.customer_name || ride.rider_name || `User ${ride.rider_id || '—'}`}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b' }}>Driver</span>
              <span style={{ fontWeight: '500' }}>
                {ride.driver_name || (ride.driver_id ? `Driver ${ride.driver_id}` : 'Not assigned')}
              </span>
            </div>

            {ride.rating && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>Rating</span>
                <span style={{ fontWeight: 'bold', color: '#f59e0b' }}>
                  ⭐ {ride.rating} {ride.rating_comment && `— ${ride.rating_comment}`}
                </span>
              </div>
            )}

            {ride.scheduled_at && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>Scheduled For</span>
                <span style={{ fontWeight: '500' }}>
                  📅 {formatDate(ride.scheduled_at)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px',
      }}>
        <h3 style={{ marginTop: 0, fontSize: '16px', fontWeight: '600' }}>
          ⏱️ Ride Timeline
        </h3>
        
        <div style={{ position: 'relative', paddingLeft: '24px', marginTop: '16px' }}>
          {[
            { label: 'Created', time: ride.created_at, icon: '📝' },
            { label: 'Assigned', time: ride.assigned_at, icon: '👤' },
            { label: 'Accepted', time: ride.accepted_at, icon: '✅' },
            { label: 'Arrived at Pickup', time: ride.driver_arrived_at, icon: '📍' },
            { label: 'Started', time: ride.started_at, icon: '🚀' },
            { label: 'Completed', time: ride.completed_at, icon: '🏁' },
            { label: 'Cancelled', time: ride.cancelled_at, icon: '❌' },
          ].map((item, index) => {
            const hasTime = item.time && new Date(item.time).getTime() > 0;
            // Filter out empty items
            if (!hasTime && item.label !== 'Created') return null;
            
            return (
              <div key={index} style={{ position: 'relative', paddingBottom: '16px' }}>
                {/* Timeline line */}
                {index < 6 && (
                  <div style={{
                    position: 'absolute',
                    left: '-4px',
                    top: '20px',
                    bottom: '0',
                    width: '2px',
                    backgroundColor: hasTime ? '#e2e8f0' : '#f1f5f9',
                  }} />
                )}
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: hasTime ? '#dcfce7' : '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ 
                      fontWeight: hasTime ? '500' : '400', 
                      color: hasTime ? '#1e293b' : '#94a3b8' 
                    }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {hasTime ? formatDate(item.time) : '—'}
                      {hasTime && ` (${getTimeAgo(item.time)})`}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginTop: 0, fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
          🔗 Actions
        </h3>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {ride.rider_id && (
            <button
              onClick={() => navigate(`/customers/${ride.rider_id}`)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
              }}
            >
              👤 View Customer
            </button>
          )}
          
          {ride.driver_id && (
            <button
              onClick={() => navigate(`/drivers/${ride.driver_id}`)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
              }}
            >
              🛵 View Driver
            </button>
          )}
          
          {ride.pickup_lat && ride.pickup_lng && ride.dropoff_lat && ride.dropoff_lng && (
            <button
              onClick={() => {
                const url = `https://www.google.com/maps/dir/${ride.pickup_lat},${ride.pickup_lng}/${ride.dropoff_lat},${ride.dropoff_lng}`;
                window.open(url, '_blank');
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
              }}
            >
              🗺️ Open in Google Maps
            </button>
          )}
          
          <button
            onClick={() => {
              const details = `
                Ride #${ride.id}
                Status: ${ride.status}
                Fare: UGX ${(ride.total_fare || 0).toLocaleString()}
                Pickup: ${ride.pickup_address || 'Unknown'}
                Dropoff: ${ride.dropoff_address || 'Unknown'}
                Customer: ${ride.customer_name || ride.rider_name || '—'}
                Driver: ${ride.driver_name || '—'}
                Created: ${formatDate(ride.created_at)}
              `;
              navigator.clipboard.writeText(details);
              alert('✅ Ride details copied to clipboard!');
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#64748b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
            }}
          >
            📋 Copy Details
          </button>
          
          <button
            onClick={fetchRideDetails}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

export default RideDetails;